import debug from "debug";
const log = debug("fetch-in-place");

class FetchInPlace {
	constructor(element, options = {}) {
		this.element = element;
		this.options = options;

		this.state = {
			href: window.location.href
		};

		this.init();
	}

	init() {
		log('FetchInPlace', this.element, this.options);

		if(!this.element.id) {
			log("Element needs an id. Aborting.");
			return false;
		}

		this.element.addEventListener(
			"click",
			this.delegatedClickHandler.bind(this)
		);
		window.onpopstate = this.historyHandler.bind(this);

	}

	get loadingIndicator() {
		return document.getElementById(this.options.loadingIndicatorId);
	}

	showLoading() {
		if(!this.loadingIndicator) {
			return false;
		}
		this.loadingIndicator.classList.add("active");
	}

	getLinkToFetch(target) {
		const anchor = getTargetAnchor(target);

		if (
			anchor &&
			isMatchingSelector(anchor, this.options.linkSelector) &&
			isInPath(anchor.href, this.options.rootPathname) &&
			isContainedBy(anchor, this.options.fetchLinksParentSelector)
		) {
			return anchor;
		}

		function getTargetAnchor(target) {
			if (target.nodeName.toLowerCase() === "a") {
				return target;
			}
			return target.closest("a");
		}

		function isMatchingSelector(element, selector) {
			return selector
				? element.matches(selector)
				: true;
		}

		function isContainedBy(element, parentSelector) {
			return element.closest(parentSelector);
		}

		function isInPath(url, path) {
			if(!path) {
				return true;
			}
			return url && url.indexOf(path) !== -1;
		}
	}

	fetchHandler(url) {
		this.showLoading();

		return fetch(url)
			.then((response) => {
				if (response.ok) {
					return response.text();
				}
				return Promise.reject(response);
			})
			.then((html) => {
				const parser = new DOMParser();
				const freshPage = parser.parseFromString(html, "text/html");
				const freshContainer = freshPage.querySelector(`#${this.element.id}`);
				const freshTitle = freshPage.title;

				emptyElement(this.element);

				if(!freshContainer) {
					throw new Error("Non-matching page template");
				}

				Array.from(freshContainer.childNodes).forEach((node) => {
					this.element.append(node);
				});

				document.title = freshTitle;

				this.exitLoading();

				return freshTitle;
			})
			.catch((error) => {

				// fallback to updating location directly
				window.location = url;
				log("error", error);
			});
	}

	delegatedClickHandler(event) {
		log("click handled:");

		const ajaxAnchorClicked = this.getLinkToFetch(event.target);
		if (ajaxAnchorClicked) {
			const href = ajaxAnchorClicked.href;
			const isSamePathWithHash = isSamePageWithoutHash(
				window.location.href,
				href
			);
			const isNewPage = hasChangedPathOrQuery(
				window.location.href,
				href
			);

			if (!isNewPage && isSamePathWithHash) {
				event.preventDefault();
				history.pushState({}, "", href);
				focusSection(this.element, href);
			} else if (isNewPage) {
				event.preventDefault();
				log("link hijacked", event, ajaxAnchorClicked, href);

				this.showLoading();

				this.fetchHandler(href).then((freshTitle) => {
					// let stateObj = { foo: "bar" };
					// get title from returned page
					history.pushState({}, freshTitle, href);
					this.state.href = href;
					focusSection(this.element, href);
				});
			} else {
				log("same page: default permitted");
			}
		}
	}

	exitLoading() {
		if (!this.loadingIndicator) {
			return false;
		}
		this.loadingIndicator.addEventListener("transitionend", hideLoading.bind(this));
		this.loadingIndicator.classList.add("is-exiting");

		function hideLoading() {
			this.loadingIndicator.classList.remove("is-exiting");
			this.loadingIndicator.classList.remove("active");
		}
	}

	historyHandler() {
		window.setTimeout(this.afterStatePop.bind(this), 0);
	}

	afterStatePop() {
		const isSamePathWithHash = isSamePageWithoutHash(this.state.href, document.location.href);
		const isNewPage = hasChangedPathOrQuery(this.state.href, document.location.href);

		if (!isNewPage && isSamePathWithHash) {
			focusSection(this.element, document.location.href);
		} else if (isNewPage) {
			this.fetchHandler(document.location).then(() => {
				focusSection(this.element, document.location.href);
			});
		}

		this.state.href = document.location.href;
	}


	// destroy() {}
}


function focusSection(ajaxElement, href) {
	const location = new URL(href);
	const hash = location.hash;

	window.setTimeout(() => {
		const hasHash = hash !== "";
		// Focus the h1 (or labelled section) to
		// cause screenreaders to read out the newly loaded content title
		const targetElement = document.querySelector(hasHash ? hash : "h1");
		targetElement && targetElement.focus({preventScroll:true});

		scrollToAnchor(hasHash ? targetElement : ajaxElement);
	}, 0);
}

function scrollToAnchor(targetElement) {
	if (!targetElement) {
		window.scroll({ top: 0, left: 0, behavior: "smooth" });
	}
}

function hasChangedPathOrQuery(prevHref, newHref) {
	if (newHref === prevHref) {
		return false;
	}
	const prevLocation = new URL(prevHref);
	const newLocation = new URL(newHref);

	const searchChanged = newLocation.search !== prevLocation.search;
	const pathChanged = newLocation.pathname !== prevLocation.pathname;

	return searchChanged || pathChanged;
}

function isSamePageWithoutHash(prevHref, newHref) {
	const prevLocation = new URL(prevHref);
	const newLocation = new URL(newHref);
	if (prevLocation.pathname !== newLocation.pathname) {
		return false;
	}
	return newLocation.hash === "";
}

function emptyElement(element) {
	while (element.firstChild) {
		element.removeChild(element.lastChild);
	}
}

FetchInPlace.DEFAULTS = {
	fetchLinksParentSelector: "[data-fetch-in-place]",
};

export default FetchInPlace;



// WEBPACK FOOTER //
// ./client/src/core/modules/fetch-in-place/fetch-in-place.js